/**
 * Banner shown above the working sheet when one or more rooftops in
 * the dealer group have no Adesa STORE → display_label mapping.
 *
 * The columns still render for unmapped rooftops (so the operator sees
 * the full column set), but disposition can't auto-route to them. This
 * banner tells the operator why those columns will stay empty.
 *
 * The [Open Preferences →] link is wishful for v1 — the Preferences UI
 * for managing aliases isn't built yet — so it renders as plain text.
 */
import React from "react"

import { AuctionUnmappedRooftop } from "../../api/wholesaleAuction"
import { THEME } from "../../config/theme"

interface Props {
  unmapped: AuctionUnmappedRooftop[]
}

export default function AuctionUnmappedRooftopBanner({ unmapped }: Props) {
  if (unmapped.length === 0) return null

  const labels = unmapped.map((r) => r.display_label).join(", ")
  const plural = unmapped.length === 1 ? "rooftop isn't" : "rooftops aren't"

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: THEME.spacing.sm,
        padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
        marginBottom: THEME.spacing.md,
        background: "rgba(217, 119, 6, 0.08)",
        border: `0.5px solid rgba(217, 119, 6, 0.3)`,
        borderRadius: THEME.borderRadius.md,
        fontSize: 13,
        color: THEME.colors.neutral.light.textPrimary,
        fontFamily: THEME.fonts.primary,
      }}
    >
      <span
        aria-hidden
        style={{
          color: THEME.colors.semantic.warning,
          fontSize: 16,
          lineHeight: 1,
        }}
      >
        ▲
      </span>
      <span>
        {unmapped.length} {plural} connected to Adesa data yet (<strong>{labels}</strong>).
        Cars for those rooftops won't auto-route until an alias is set up.
        <span
          style={{
            marginLeft: 8,
            color: THEME.colors.neutral.light.textSecondary,
            fontStyle: "italic",
          }}
        >
          Configure in Wholesale → Preferences (coming soon).
        </span>
      </span>
    </div>
  )
}
